import React from 'react';
import { Link } from 'react-router';
import { PageHero } from '../components/PageHero';
import { Section } from '../components/Section';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { IconWrap } from '../components/IconWrap';
import useTheme from '../hooks/useTheme';
import { megaGreenData } from '../data/data';
import { CiCircleCheck, CiStar, CiLock } from 'react-icons/ci';
import { BiTargetLock, BiTimeFive, BiCheckCircle, BiClipboard, BiTrendingUp } from 'react-icons/bi';
import { HiOutlineLightBulb } from 'react-icons/hi';
import { PiLeafThin } from "react-icons/pi";


export const About: React.FC = () => {
    const { theme, breakpoint } = useTheme();
    const about = megaGreenData.about;

    return (
        <>
            <PageHero
                title="About Mega Green"
                subtitle={about.intro}
            />

            <Section>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: breakpoint.xs || breakpoint.sm ? '1fr' : '1fr 1fr',
                        gap: '48px',
                        alignItems: 'center',
                    }}
                >
                    <div>
                        <h2
                            style={{
                                fontSize: breakpoint.xs || breakpoint.sm ? '28px' : '34px',
                                fontWeight: 700,
                                color: theme.text.primary,
                                marginBottom: '24px',
                            }}
                        >
                            Who we are
                        </h2>
                        <p
                            style={{
                                fontSize: '16px',
                                color: theme.text.muted,
                                lineHeight: 1.6,
                            }}
                        >
                            {about.whoWeAre}
                        </p>
                    </div>
                    <div>
                        <img src={about.src} alt="About" style={{ width: '100%', height: 300, objectFit: 'cover', borderRadius: theme.borderRadius.r1 }} />
                    </div>
                </div>
            </Section>

            <Section index={1}>
                <Card
                    style={{
                        maxWidth: '800px',
                        margin: '0 auto',
                        textAlign: 'center',
                    }}
                    variant="primary"
                >
                    <IconWrap
                        icon={<BiTargetLock />}
                        size={48}
                        color="primary"
                        withBackground={true}
                        style={{ marginBottom: '24px' }}
                    />
                    <h2
                        style={{
                            fontSize: breakpoint.xs || breakpoint.sm ? '28px' : '34px',
                            fontWeight: 700,
                            color: theme.text.primary,
                            marginBottom: '24px',
                        }}
                    >
                        Our Mission
                    </h2>
                    <p
                        style={{
                            fontSize: '18px',
                            color: theme.text.muted,
                            lineHeight: 1.6,
                        }}
                    >
                        {about.mission}
                    </p>
                </Card>
            </Section>

            <Section index={2}>
                <h2
                    style={{
                        fontSize: breakpoint.xs || breakpoint.sm ? '28px' : '34px',
                        fontWeight: 700,
                        color: theme.text.primary,
                        marginBottom: '48px',
                        textAlign: 'center',
                    }}
                >
                    Our Values
                </h2>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: breakpoint.xs || breakpoint.sm ? '1fr' : breakpoint.md ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)',
                        gap: '24px',
                    }}
                >
                    {about.values.map((value, idx) => {
                        const valueIcons = [
                            <PiLeafThin />, // Sustainability
                            <HiOutlineLightBulb />, // Innovation
                            <CiStar />, // Excellence
                            <CiLock />, // Integrity
                            <BiTargetLock />, // Partnership
                        ];
                        const valueColors: Array<'primary' | 'secondary' | 'accent' | 'highlight' | 'feature'> = [
                            'primary',
                            'highlight',
                            'accent',
                            'secondary',
                            'feature',
                        ];

                        return (
                            <Card key={idx} hoverable variant={idx % 2 === 0 ? 'primary' : 'secondary'}>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                    }}
                                >
                                    <IconWrap
                                        icon={valueIcons[idx] || <CiCircleCheck />}
                                        size={40}
                                        color={valueColors[idx] || 'primary'}
                                        withBackground={true}
                                        style={{ marginBottom: '16px' }}
                                    />
                                    <h3
                                        style={{
                                            fontSize: '18px',
                                            fontWeight: 600,
                                            color: theme.text.primary,
                                            marginBottom: '12px',
                                        }}
                                    >
                                        {value.title}
                                    </h3>
                                    <p
                                        style={{
                                            fontSize: '14px',
                                            color: theme.text.muted,
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {value.desc}
                                    </p>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            </Section>

            <Section index={3}>
                <h2
                    style={{
                        fontSize: breakpoint.xs || breakpoint.sm ? '28px' : '34px',
                        fontWeight: 700,
                        color: theme.text.primary,
                        marginBottom: '48px',
                        textAlign: 'center',
                    }}
                >
                    How we work
                </h2>
                <div
                    style={{
                        display: breakpoint.xs || breakpoint.sm ? 'flex' : 'grid',
                        flexDirection: breakpoint.xs || breakpoint.sm ? 'column' : undefined,
                        gridTemplateColumns: breakpoint.xs || breakpoint.sm ? undefined : 'repeat(4, 1fr)',
                        gap: '24px',
                        position: 'relative',
                    }}
                >
                    {!breakpoint.xs && !breakpoint.sm && (
                        <div
                            style={{
                                position: 'absolute',
                                top: '48px',
                                left: '48px',
                                right: '48px',
                                height: '3px',
                                background: theme.gradients.button,
                                borderRadius: '2px',
                                zIndex: 0,
                            }}
                        />
                    )}
                    {about.howWeWorkSteps.map((step, idx) => {
                        const stepIcons = [
                            <BiTimeFive />, // Initial Consultation
                            <BiClipboard />, // Assessment & Planning
                            <BiTrendingUp />, // Implementation
                            <BiCheckCircle />, // Monitoring & Support
                        ];
                        const stepColors: Array<'primary' | 'secondary' | 'accent' | 'highlight'> = [
                            'primary',
                            'secondary',
                            'accent',
                            'highlight',
                        ];

                        return (
                            <div
                                key={idx}
                                style={{
                                    position: 'relative',
                                    zIndex: 1,
                                }}
                            >
                                <Card hoverable variant={idx % 2 === 0 ? 'primary' : 'secondary'}
                                    style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: '64px',
                                                height: '64px',
                                                borderRadius: '50%',
                                                background: theme.gradients.button,
                                                color: theme.text.contrast,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '24px',
                                                fontWeight: 700,
                                                marginBottom: '16px',
                                                boxShadow: theme.shadow.cardShadow,
                                            }}
                                        >
                                            {idx + 1}
                                        </div>
                                        <IconWrap
                                            icon={stepIcons[idx] || <BiCheckCircle />}
                                            size={32}
                                            color={stepColors[idx] || 'primary'}
                                            withBackground={true}
                                            style={{ marginBottom: '16px' }}
                                        />
                                        <h3
                                            style={{
                                                fontSize: '18px',
                                                fontWeight: 600,
                                                color: theme.text.primary,
                                                marginBottom: '12px',
                                                textAlign: 'center',
                                            }}
                                        >
                                            {step.title}
                                        </h3>
                                        <p
                                            style={{
                                                fontSize: '14px',
                                                color: theme.text.muted,
                                                textAlign: 'center',
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            {step.desc}
                                        </p>
                                    </div>
                                </Card>
                            </div>
                        );
                    })}
                </div>
            </Section>

            <Section
                style={{
                    backgroundColor: theme.background.panel,
                }}
            >
                <div
                    style={{
                        maxWidth: '800px',
                        margin: '0 auto',
                        textAlign: 'center',
                    }}
                >
                    <h2
                        style={{
                            fontSize: breakpoint.xs || breakpoint.sm ? '28px' : '34px',
                            fontWeight: 700,
                            color: theme.text.primary,
                            marginBottom: '16px',
                        }}
                    >
                        Tell us what you're planning
                    </h2>
                    <p
                        style={{
                            fontSize: '16px',
                            color: theme.text.muted,
                            marginBottom: '24px',
                        }}
                    >
                        {megaGreenData.contact.pageIntro}
                    </p>
                    <Link to="/contact" style={{ textDecoration: 'none' }}>
                        <Button variant="primary" style={{ padding: '12px 32px' }}>
                            Request a Consultation
                        </Button>
                    </Link>
                </div>
            </Section>
        </>
    );
};
